import {
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format, isFuture, max } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../app/hooks";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import DeleteButton from "../../../../common/components/functional/formTable/DeleteButton";
import LoadingSpinner from "../../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { isExtraComponent } from "../../../../hooks/useTesSalaryComponents";
import { SalaryPerDate } from "../../../employment/employmentApi";
import { getZoneLabel } from "./common";

type SalaryTableRow = {
  originalSalaryPerDate: SalaryPerDate;
  date: string;
  salary: { [key: string]: string };
  zone?: string; // undefined for "Muu Suomi", zone name for zoned salary
  uniqueKey: string;
};

type Props = {
  tableHeaders: string[];
  tableData: SalaryPerDate[];
  selectedSalary: SalaryPerDate | null;
  selectedZone?: string;
  handleSelectSalary: (salaryPerDate: SalaryPerDate, zone?: string) => void;
  handleDeleteSalary: (id: string) => void;
  disableRowChange?: boolean;
};

const SalariesTable = ({
  tableHeaders,
  tableData,
  selectedSalary,
  selectedZone,
  handleSelectSalary,
  handleDeleteSalary,
  disableRowChange,
}: Props) => {
  const { status } = useAppSelector(
    (state) => state.employment.attributesForEditing,
  );
  const isLoading = status === "pending";

  const { salaryComponent } = useAppSelector((state) => state.localized);
  const { t } = useTranslation();

  const hasZonedSalaries = tableData.some(
    (salary) =>
      salary.zonedSalary && Object.keys(salary.zonedSalary).length > 0,
  );

  const tableRows: SalaryTableRow[] = tableData.flatMap((salaryPerDate) => {
    const rows: SalaryTableRow[] = [];

    if (
      salaryPerDate.zonedSalary &&
      Object.keys(salaryPerDate.zonedSalary).length > 0
    ) {
      // add "Muu Suomi" row
      rows.push({
        originalSalaryPerDate: salaryPerDate,
        date: salaryPerDate.date,
        salary: salaryPerDate.salary,
        zone: undefined, // Muu Suomi
        uniqueKey: `${salaryPerDate.date}_muusuomi`,
      });

      // add zoned rows
      Object.entries(salaryPerDate.zonedSalary).forEach(
        ([zone, zoneSalary]) => {
          rows.push({
            originalSalaryPerDate: salaryPerDate,
            date: salaryPerDate.date,
            salary: zoneSalary,
            zone: zone,
            uniqueKey: `${salaryPerDate.date}_${zone}`,
          });
        },
      );
    } else {
      // no zones, add single row
      rows.push({
        originalSalaryPerDate: salaryPerDate,
        date: salaryPerDate.date,
        salary: salaryPerDate.salary,
        zone: undefined,
        uniqueKey: salaryPerDate.date,
      });
    }

    return rows;
  });

  const formatSalary = (value: string) => {
    if (isNaN(+value)) return value;
    const parsedValue = parseFloat(value).toFixed(2);
    const formattedValue = parsedValue.replace(".", ",");
    return `${formattedValue} €/h`;
  };

  const calculateTotalSalary = (salary: { [key: string]: string }) => {
    const totalSalary = Object.keys(salary).reduce((acc, key) => {
      const value = salary[key];
      if (!isExtraComponent(key)) {
        acc = +acc + +value;
      }
      return acc;
    }, 0);

    return formatSalary(totalSalary.toString());
  };

  const fillMissingSalaries = (salaryObj: { [key: string]: string }) => {
    const filledSalaryObj: { [key: string]: string } = {};
    tableHeaders.forEach((header) => {
      if (salaryObj.hasOwnProperty(header)) {
        filledSalaryObj[header] = salaryObj[header];
      } else {
        filledSalaryObj[header] = "-";
      }
    });
    return filledSalaryObj;
  };

  const getTextColor = (date: string) =>
    isFuture(new Date(date)) ? "text.secondary" : "text.primary";

  const containsRemovableSalaries = tableData.some((item) =>
    isFuture(new Date(item.date)),
  );

  const isLatestDate = (date: string) => {
    const latestDate = max(tableData.map((item) => new Date(item.date)));
    return latestDate && latestDate.getTime() === new Date(date).getTime();
  };

  const isRowSelected = (row: SalaryTableRow) => {
    return selectedSalary?.date === row.date && selectedZone === row.zone;
  };

  return (
    <FormSection
      title={t("talentProfile:salarySettings")}
      noBorder
      disablePaddingBottom
      margin
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <TableContainer sx={{ marginLeft: "16px" }}>
          <Table>
            <TableHead>
              <TableRow>
                {hasZonedSalaries && (
                  <TableCell
                    sx={{
                      padding: "4px 8px 8px 0px",
                      verticalAlign: "bottom",
                    }}
                    align="left"
                  >
                    <Typography
                      fontSize="13px"
                      lineHeight="12px"
                      letterSpacing="0.1px"
                    >
                      {t("common:area")}
                    </Typography>
                  </TableCell>
                )}
                <TableCell
                  width="5%"
                  sx={{
                    padding: "4px 8px 8px",
                    verticalAlign: "bottom",
                  }}
                  align={hasZonedSalaries ? "center" : "left"}
                >
                  <Typography
                    fontSize="13px"
                    lineHeight="12px"
                    letterSpacing="0.1px"
                    whiteSpace="nowrap"
                    textAlign="left"
                  >
                    {t("talentProfile:validFrom")}
                  </Typography>
                </TableCell>
                {tableHeaders.map((header) => (
                  <TableCell
                    sx={{
                      padding: "4px 8px 8px 8px",
                      verticalAlign: "bottom",
                    }}
                    align="right"
                    key={header}
                  >
                    <Typography
                      fontSize="13px"
                      lineHeight="12px"
                      letterSpacing="0.1px"
                    >
                      {salaryComponent[header] || header}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    padding: "4px 0px 8px 8px",
                    verticalAlign: "bottom",
                  }}
                  align="right"
                >
                  <Typography lineHeight="12px" fontSize="13px">
                    {t("talentProfile:totalSalary")}
                  </Typography>
                </TableCell>
                {containsRemovableSalaries && (
                  <TableCell sx={{ padding: "4px 4px 8px 4px" }} />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row) => (
                <TableRow
                  key={row.uniqueKey}
                  sx={{
                    cursor: disableRowChange ? "default" : "pointer",
                    "&.Mui-selected": {
                      backgroundColor: (theme) =>
                        theme.palette.background.default,
                      "&:hover": {
                        backgroundColor: (theme) =>
                          alpha(theme.palette.background.default, 0.8),
                      },
                    },
                    "& > td": {
                      color: getTextColor(row.date),
                    },
                  }}
                  selected={isRowSelected(row)}
                  onClick={() => {
                    if (!disableRowChange)
                      handleSelectSalary(row.originalSalaryPerDate, row.zone);
                  }}
                >
                  {hasZonedSalaries && (
                    <TableCell sx={{ padding: "4px 8px 8px 0px" }} align="left">
                      <Typography lineHeight="19px" fontSize="16px">
                        {getZoneLabel(row.zone)}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{ padding: "8px 8px 8px" }}
                    align={hasZonedSalaries ? "center" : "right"}
                  >
                    <Typography
                      lineHeight="19px"
                      fontSize="16px"
                      textAlign={hasZonedSalaries ? "center" : "right"}
                    >
                      {format(new Date(row.date), "dd.MM.yyyy")}
                    </Typography>
                  </TableCell>
                  {Object.values(fillMissingSalaries(row.salary)).map(
                    (value, index) => (
                      <TableCell
                        key={`${index}_${value}`}
                        sx={{ padding: "8px 8px" }}
                        align="right"
                      >
                        <Typography lineHeight="19px" fontSize="16px">
                          {formatSalary(value)}
                        </Typography>
                      </TableCell>
                    ),
                  )}
                  <TableCell
                    width="10%"
                    sx={{
                      padding: "4px 0px 8px 8px",
                    }}
                    align="right"
                  >
                    <Typography
                      lineHeight="19px"
                      fontSize="16px"
                      fontWeight="600"
                      whiteSpace="nowrap"
                    >
                      {calculateTotalSalary(row.salary)}
                    </Typography>
                  </TableCell>
                  {containsRemovableSalaries && (
                    <TableCell
                      sx={{
                        padding: "0px 0px",
                      }}
                      align="right"
                    >
                      {isLatestDate(row.date) && row.zone === undefined && (
                        <DeleteButton
                          sxConfirm={{ maxHeight: "30px" }}
                          handleDelete={(id) => handleDeleteSalary(id)}
                          id={row.date}
                        />
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </FormSection>
  );
};

export default SalariesTable;
